class CartDrawer extends HTMLElement {
  constructor() {
    super();
    this.backdrop = this.querySelector('[data-cart-backdrop]');
    this.closeButtons = this.querySelectorAll('[data-cart-close]');
    this.countTargets = document.querySelectorAll('[data-cart-count]');
    this.openButtons = document.querySelectorAll('[data-open-cart]');
    this.body = this.querySelector('[data-cart-body]');
    this.footerSubtotal = this.querySelector('[data-cart-subtotal]');
    this.progressMessage = this.querySelector('[data-free-shipping-message]');
    this.progressFill = this.querySelector('[data-free-shipping-fill]');
    this.notice = this.querySelector('[data-cart-notice]');
    this.checkoutButton = this.querySelector('[data-cart-checkout]');
    this.clearButton = this.querySelector('[data-cart-clear]');
    this.upsell = this.querySelector('[data-cart-upsell]');
  }

  connectedCallback() {
    this.backdrop?.addEventListener('click', () => this.close());
    this.closeButtons.forEach((button) => button.addEventListener('click', () => this.close()));
    document.addEventListener('click', (event) => {
      const trigger = event.target.closest('[data-open-cart]');

      if (trigger) {
        event.preventDefault();
        this.open();
      }

      const quantityButton = event.target.closest('[data-quantity-button]');
      if (quantityButton) {
        event.preventDefault();
        const input = quantityButton.parentElement.querySelector('input');
        const step = Number(quantityButton.dataset.quantityButton);
        input.value = Math.max(0, Number(input.value || 0) + step);
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }

      const removeButton = event.target.closest('[data-cart-remove]');
      if (removeButton) {
        event.preventDefault();
        this.updateItem(removeButton.dataset.cartRemove, 0);
      }
    });

    this.body?.addEventListener('change', (event) => {
      const input = event.target.closest('[data-cart-quantity-input]');
      if (input) {
        this.updateItem(input.dataset.cartQuantityInput, Number(input.value));
      }
    });

    this.clearButton?.addEventListener('click', () => this.clearCart());
  }

  open() {
    this.setAttribute('open', '');
    document.body.classList.add('overflow-hidden');
    pushDataLayer('view_cart', {
      ecommerce: {
        currency: window.EaseHoundTheme?.currency || 'USD'
      }
    });
  }

  close() {
    this.removeAttribute('open');
    document.body.classList.remove('overflow-hidden');
  }

  async render() {
    const response = await fetch(`${window.Shopify.routes.root}cart.js`);
    const cart = await response.json();
    const template = this.querySelector('#CartDrawerItemTemplate');

    if (this.body && template) {
      this.body.innerHTML = '';

      if (cart.items.length === 0) {
        this.body.innerHTML = '<p>Your cart is empty. Explore EaseHound essentials for rest, movement, travel, and mealtime.</p>';
      } else {
        cart.items.forEach((item) => {
          const bundleName = item.properties?.['Recommended setup'] || item.properties?.Bundle || '';
          const bundleLabelText = bundleName ? `Recommended setup: ${bundleName}` : '';
          const fragment = template.content.cloneNode(true);
          const insertedItem = fragment.querySelector('.cart-item');
          const itemUrl = insertedItem?.querySelector('[data-cart-item-url]');
          const itemTitle = insertedItem?.querySelector('[data-cart-item-title]');
          const itemVariant = insertedItem?.querySelector('[data-cart-item-variant]');
          const itemPrice = insertedItem?.querySelector('[data-cart-item-price]');
          const itemQuantity = insertedItem?.querySelector('[data-cart-quantity-input]');
          const itemRemove = insertedItem?.querySelector('[data-cart-remove]');
          const itemImage = insertedItem?.querySelector('[data-cart-item-image]');

          if (itemUrl) itemUrl.href = item.url;
          if (itemTitle) itemTitle.textContent = item.product_title;
          if (itemVariant && item.variant_title) {
            itemVariant.textContent = item.variant_title;
            itemVariant.hidden = false;
          }
          if (itemPrice) itemPrice.textContent = formatMoney(item.final_line_price);
          if (itemQuantity) {
            itemQuantity.value = item.quantity;
            itemQuantity.dataset.cartQuantityInput = item.key;
            itemQuantity.setAttribute('aria-label', `Quantity for ${item.product_title}`);
          }
          if (itemRemove) itemRemove.dataset.cartRemove = item.key;
          if (itemImage && item.featured_image?.url) {
            itemImage.src = item.featured_image.url;
            itemImage.alt = item.product_title;
          }
          const bundleLabel = insertedItem?.querySelector('[data-cart-item-bundle]');
          if (bundleLabel && bundleLabelText) {
            bundleLabel.textContent = bundleLabelText;
            bundleLabel.hidden = false;
          }
          this.body.appendChild(fragment);
        });
      }
    }

    this.countTargets.forEach((target) => {
      target.textContent = cart.item_count;
      target.hidden = cart.item_count === 0;
    });

    this.openButtons.forEach((button) => {
      button.setAttribute('aria-label', `Open cart, ${cart.item_count} items`);
    });

    if (this.footerSubtotal) {
      this.footerSubtotal.textContent = formatMoney(cart.total_price);
    }

    if (this.checkoutButton) {
      this.checkoutButton.disabled = cart.item_count === 0;
      this.checkoutButton.textContent = cart.item_count === 0 ? 'Cart is empty' : 'Checkout';
    }

    if (this.clearButton) {
      this.clearButton.hidden = cart.item_count === 0;
    }

    if (this.upsell) {
      const upsellProductId = Number(this.upsell.dataset.productId || 0);
      this.upsell.hidden = cart.items.some((item) => Number(item.product_id) === upsellProductId);
    }

    this.updateFreeShipping(cart.total_price);
  }

  updateFreeShipping(totalPrice) {
    const threshold = Number(this.dataset.freeShippingThreshold || 0);
    if (!threshold || !this.progressMessage || !this.progressFill) return;

    if (this.dataset.freeShippingCountry !== 'US') {
      this.progressFill.style.width = '0%';
      this.progressMessage.textContent = 'Free shipping on orders of $150 or more applies to U.S. orders only.';
      return;
    }

    const progress = Math.min(100, Math.round((totalPrice / threshold) * 100));
    const remaining = threshold - totalPrice;
    this.progressFill.style.width = `${progress}%`;

    if (remaining > 0) {
      this.progressMessage.textContent = `You're ${formatMoney(remaining)} away from free U.S. shipping.`;
    } else {
      this.progressMessage.textContent = 'Free U.S. shipping unlocked for your order.';
    }
  }

  async updateLine(line, quantity) {
    await fetch(`${window.Shopify.routes.root}cart/change.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ line, quantity })
    });
    await this.render();
  }

  async updateItem(id, quantity) {
    this.setNotice(quantity > 0 ? 'Cart updated.' : 'Item removed from cart.');
    await fetch(`${window.Shopify.routes.root}cart/change.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id, quantity: Math.max(0, quantity) })
    });
    await this.render();
  }

  async clearCart() {
    this.setNotice('Cart cleared.');
    await fetch(`${window.Shopify.routes.root}cart/clear.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' }
    });
    await this.render();
  }

  setNotice(message) {
    if (!this.notice) return;
    this.notice.textContent = message;
    this.notice.hidden = !message;
  }

  async addItems(items) {
    await fetch(`${window.Shopify.routes.root}cart/add.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items })
    });
    await this.render();
    this.open();
  }
}

const pushDataLayer = (eventName, payload = {}) => {
  window.dataLayer = window.dataLayer || [];
  const eventPayload = {
    event: eventName,
    ...payload
  };
  window.dataLayer.push(eventPayload);
};

const buildEcommerceItem = ({ variantId, productId, title, brand, variantTitle = '', price = 0, quantity = 1 }) => ({
  item_id: String(variantId || productId || title),
  item_group_id: productId ? String(productId) : undefined,
  item_name: title,
  item_brand: brand || undefined,
  item_variant: variantTitle || undefined,
  price: Number(price || 0) / 100,
  quantity
});

const addItemsAndMaybeCheckout = async ({ items, checkout = false, analyticsItem = null }) => {
  const drawer = document.querySelector('cart-drawer');
  await fetch(window.EaseHoundTheme?.routes?.cartAdd || `${window.Shopify.routes.root}cart/add.js`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ items })
  });

  if (analyticsItem) {
    pushDataLayer('add_to_cart', {
      ecommerce: {
        currency: window.EaseHoundTheme?.currency || 'USD',
        value: analyticsItem.price * analyticsItem.quantity,
        items: [analyticsItem]
      }
    });
  }

  if (checkout) {
    pushDataLayer('begin_checkout', {
      ecommerce: {
        currency: window.EaseHoundTheme?.currency || 'USD',
        value: analyticsItem ? analyticsItem.price * analyticsItem.quantity : undefined,
        items: analyticsItem ? [analyticsItem] : undefined
      }
    });
    window.location.href = window.EaseHoundTheme?.routes?.checkout || '/checkout';
    return;
  }

  if (drawer) {
    drawer.setNotice?.('Added to cart. Review quantities before checkout.');
    await drawer.render();
    drawer.open();
  }
};

const openCartWithNotice = async (message) => {
  const drawer = document.querySelector('cart-drawer');
  if (!drawer) return false;
  drawer.setNotice?.(message);
  await drawer.render();
  drawer.open();
  return true;
};

const cartAlreadyHasVariants = async (variantIds) => {
  const response = await fetch(`${window.Shopify.routes.root}cart.js`);
  const cart = await response.json();
  const currentIds = new Set(cart.items.map((item) => Number(item.variant_id)));
  return variantIds.every((id) => currentIds.has(Number(id)));
};

const formatMoney = (cents) => {
  if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
    return window.Shopify.formatMoney(cents);
  }

  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD'
  }).format((cents || 0) / 100);
};

const addBusinessDays = (startDate, businessDays) => {
  const result = new Date(startDate);
  let remaining = Math.max(0, Number(businessDays) || 0);

  while (remaining > 0) {
    result.setDate(result.getDate() + 1);
    const weekday = result.getDay();
    if (weekday !== 0 && weekday !== 6) remaining -= 1;
  }

  return result;
};

const initDeliveryWindows = () => {
  const formatter = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric' });
  const today = new Date();

  document.querySelectorAll('[data-delivery-window]').forEach((target) => {
    const earliest = addBusinessDays(today, target.dataset.minBusinessDays);
    const latest = addBusinessDays(today, target.dataset.maxBusinessDays);
    target.textContent = `${formatter.format(earliest)}–${formatter.format(latest)}`;
    target.setAttribute('aria-label', `Estimated arrival between ${formatter.format(earliest)} and ${formatter.format(latest)}, excluding holidays and carrier delays`);
  });
};

class ProductFormHandler {
  constructor(form) {
    this.form = form;
    this.section = form.closest('[data-product-section]');
    this.variantData = JSON.parse(this.section.querySelector('[data-product-variants]').textContent);
    this.variantIdInput = this.form.querySelector('[name="id"]');
    this.price = this.section.querySelector('[data-product-price]');
    this.comparePrice = this.section.querySelector('[data-product-compare-price]');
    this.galleryMain = this.section.querySelector('[data-gallery-main]');
    this.mainImage = this.section.querySelector('[data-gallery-main-image]');
    this.galleryThumbs = Array.from(this.section.querySelectorAll('[data-gallery-thumb]'));
    this.galleryPrev = this.section.querySelector('[data-gallery-prev]');
    this.galleryNext = this.section.querySelector('[data-gallery-next]');
    this.hasPremiumGallery = this.section.dataset.premiumGallery === 'true';
    this.currentGalleryIndex = Math.max(0, this.galleryThumbs.findIndex((thumb) => thumb.getAttribute('aria-current') === 'true'));
    this.touchStartX = 0;
    this.touchStartY = 0;
    this.stickyBar = document.querySelector('[data-sticky-atc]');
    this.stickyPrice = this.stickyBar?.querySelector('[data-sticky-price]');
    this.stickyVariant = this.stickyBar?.querySelector('[data-sticky-variant]');
    this.productTitle = this.section.dataset.productTitle;
    this.productVendor = this.section.dataset.productVendor;
    this.productId = this.section.dataset.productId;
    this.primarySubmitButton = this.form.querySelector('[type="submit"]');
    this.bundleButtons = this.section.querySelectorAll('[data-bundle-add]');
    this.bundleCards = this.section.querySelectorAll('[data-product-bundle-card]');
    this.form.addEventListener('change', () => this.onVariantChange());
    this.form.addEventListener('submit', (event) => this.onSubmit(event));
    this.form.querySelector('[data-buy-now-button]')?.addEventListener('click', () => this.onBuyNow());
    this.bundleButtons.forEach((button) => button.addEventListener('click', () => this.onBundleAdd(button)));
    this.bundleCards.forEach((card) => {
      card.addEventListener('click', (event) => {
        if (event.target.closest('a, button')) return;
        this.bundleCards.forEach((deal) => deal.classList.toggle('is-selected', deal === card));
      });
    });
    this.setupGallery();
    this.onVariantChange();
    this.watchSticky();
    this.trackViewItem();
  }

  getCurrentOptions() {
    return Array.from(this.form.querySelectorAll('fieldset')).map((fieldset) => {
      const checked = fieldset.querySelector('[data-option-index]:checked');
      return checked ? checked.value : '';
    });
  }

  getCurrentVariant() {
    const options = this.getCurrentOptions();
    return this.variantData.find((variant) => variant.options.every((option, index) => option === options[index]));
  }

  onVariantChange() {
    const variant = this.getCurrentVariant();
    if (!variant) return;
    this.variantIdInput.value = variant.id;

    if (this.price) this.price.textContent = formatMoney(variant.price);
    if (this.stickyPrice) this.stickyPrice.textContent = formatMoney(variant.price);
    if (this.stickyVariant) {
      this.stickyVariant.textContent = variant.title;
      this.stickyVariant.hidden = variant.title === 'Default Title';
    }
    if (this.comparePrice) {
      if (variant.compare_at_price > variant.price) {
        this.comparePrice.textContent = formatMoney(variant.compare_at_price);
        this.comparePrice.hidden = false;
      } else {
        this.comparePrice.hidden = true;
      }
    }

    if (this.hasPremiumGallery && this.mainImage) {
      const colorIndex = this.galleryThumbs.findIndex((thumb) =>
        thumb.dataset.galleryColor && variant.options.some((option) =>
          option.toLowerCase().includes(thumb.dataset.galleryColor.toLowerCase())
        )
      );
      if (colorIndex >= 0) this.showGalleryImage(colorIndex);
    } else if (variant.featured_image && this.mainImage) {
      const matchingIndex = this.findGalleryIndex(variant.featured_image.src);
      if (matchingIndex >= 0) {
        this.showGalleryImage(matchingIndex);
      } else {
        this.updateMainImage(variant.featured_image.src, variant.featured_image.alt || variant.name);
      }
    }

    if (this.primarySubmitButton) {
      this.primarySubmitButton.disabled = !variant.available;
      this.primarySubmitButton.textContent = variant.available
        ? this.primarySubmitButton.dataset.availableLabel
        : this.primarySubmitButton.dataset.soldOutLabel;
    }

    pushDataLayer('select_item_variant', {
      ecommerce: {
        currency: window.EaseHoundTheme?.currency || 'USD',
        value: Number(variant.price || 0) / 100,
        items: [
          buildEcommerceItem({
            variantId: variant.id,
            productId: this.productId,
            title: this.productTitle,
            brand: this.productVendor,
            variantTitle: variant.name,
            price: variant.price,
            quantity: 1
          })
        ]
      }
    });

    const stickyId = this.stickyBar?.querySelector('[name="id"]');
    if (stickyId) stickyId.value = variant.id;
  }

  async onSubmit(event) {
    event.preventDefault();
    const variant = this.getCurrentVariant();
    if (!variant?.available) return;
    const formData = new FormData(this.form);
    const quantity = Number(formData.get('quantity')) || 1;
    await addItemsAndMaybeCheckout({
      items: [{ id: Number(formData.get('id')), quantity }],
      analyticsItem: buildEcommerceItem({
        variantId: variant?.id,
        productId: this.productId,
        title: this.productTitle,
        brand: this.productVendor,
        variantTitle: variant?.name,
        price: variant?.price,
        quantity
      })
    });
  }

  async onBuyNow() {
    const variant = this.getCurrentVariant();
    if (!variant || !variant.available) return;
    const quantity = Number(this.form.querySelector('[name="quantity"]')?.value || 1);
    await addItemsAndMaybeCheckout({
      items: [{ id: Number(variant.id), quantity }],
      checkout: true,
      analyticsItem: buildEcommerceItem({
        variantId: variant.id,
        productId: this.productId,
        title: this.productTitle,
        brand: this.productVendor,
        variantTitle: variant.name,
        price: variant.price,
        quantity
      })
    });
  }

  async onBundleAdd(button) {
    const variant = this.getCurrentVariant();
    if (!variant || !variant.available) return;
    const quantity = Number(this.form.querySelector('[name="quantity"]')?.value || 1);
    const partnerIds = String(button.dataset.bundleVariantIds || '')
      .split(',')
      .map((id) => Number(id.trim()))
      .filter(Boolean);
    const bundleTitle = button.dataset.bundleTitle || `${this.productTitle} setup`;
    const bundleProperties = { 'Recommended setup': bundleTitle };
    button.closest('[data-product-bundle]')?.querySelectorAll('[data-product-bundle-card]').forEach((card) => {
      card.classList.toggle('is-selected', card.contains(button));
    });
    const items = [
      { id: Number(variant.id), quantity, properties: bundleProperties },
      ...partnerIds.map((id) => ({ id, quantity: 1, properties: bundleProperties }))
    ];
    const bundleVariantIds = items.map((item) => item.id);

    button.disabled = true;
    const originalLabel = button.textContent;
    button.textContent = 'Adding setup...';

    try {
      if (await cartAlreadyHasVariants(bundleVariantIds)) {
        await openCartWithNotice('This bundle is already in your cart. Adjust quantities or remove items before checkout.');
        return;
      }

      await addItemsAndMaybeCheckout({
        items,
        analyticsItem: buildEcommerceItem({
          variantId: variant.id,
          productId: this.productId,
          title: bundleTitle,
          brand: this.productVendor,
          variantTitle: variant.name,
          price: variant.price,
          quantity: items.length
        })
      });
    } finally {
      button.disabled = false;
      button.textContent = originalLabel;
    }
  }

  setupGallery() {
    if (!this.mainImage || this.galleryThumbs.length === 0) return;

    this.galleryThumbs.forEach((button, index) => {
      button.addEventListener('click', () => this.showGalleryImage(index, true));
    });

    this.galleryPrev?.addEventListener('click', () => this.stepGallery(-1));
    this.galleryNext?.addEventListener('click', () => this.stepGallery(1));

    this.galleryMain?.addEventListener('touchstart', (event) => {
      const touch = event.touches[0];
      this.touchStartX = touch.clientX;
      this.touchStartY = touch.clientY;
    }, { passive: true });

    this.galleryMain?.addEventListener('touchend', (event) => {
      const touch = event.changedTouches[0];
      const deltaX = touch.clientX - this.touchStartX;
      const deltaY = touch.clientY - this.touchStartY;
      if (Math.abs(deltaX) < 42 || Math.abs(deltaX) < Math.abs(deltaY)) return;
      this.stepGallery(deltaX > 0 ? -1 : 1);
    }, { passive: true });

    this.showGalleryImage(this.currentGalleryIndex);
  }

  normalizeImageUrl(src) {
    return String(src || '')
      .split('?')[0]
      .replace(/^https?:/, '')
      .replace(/_(\\d+x\\d+|\\d+x|x\\d+)\\./, '.');
  }

  findGalleryIndex(src) {
    const normalizedSrc = this.normalizeImageUrl(src);
    return this.galleryThumbs.findIndex((thumb) => this.normalizeImageUrl(thumb.dataset.galleryThumb) === normalizedSrc);
  }

  updateMainImage(src, alt) {
    if (!this.mainImage || !src) return;
    this.mainImage.src = src;
    this.mainImage.srcset = '';
    if (alt) this.mainImage.alt = alt;
  }

  showGalleryImage(index, trackInteraction = false) {
    if (!this.mainImage || this.galleryThumbs.length === 0) return;
    const nextIndex = (index + this.galleryThumbs.length) % this.galleryThumbs.length;
    const activeThumb = this.galleryThumbs[nextIndex];
    this.currentGalleryIndex = nextIndex;
    this.updateMainImage(activeThumb.dataset.galleryThumb, activeThumb.dataset.galleryAlt || this.productTitle);

    this.galleryThumbs.forEach((thumb, thumbIndex) => {
      const isActive = thumbIndex === nextIndex;
      thumb.classList.toggle('is-active', isActive);
      if (isActive) {
        thumb.setAttribute('aria-current', 'true');
        thumb.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
      } else {
        thumb.removeAttribute('aria-current');
      }
    });

    if (trackInteraction) {
      pushDataLayer('product_gallery_interaction', {
        item_name: this.productTitle,
        item_id: this.productId,
        image_index: nextIndex + 1
      });
    }
  }

  stepGallery(direction) {
    if (this.galleryThumbs.length <= 1) return;
    this.showGalleryImage(this.currentGalleryIndex + direction, true);
  }

  watchSticky() {
    if (!this.stickyBar) return;
    const purchaseArea = this.form.querySelector('.button-stack') || this.form;
    const gallery = this.section.querySelector('.product-gallery');
    if (!purchaseArea) return;

    const updateSticky = () => {
      const purchaseRect = purchaseArea.getBoundingClientRect();
      const purchaseVisible = purchaseRect.top < window.innerHeight - 24 && purchaseRect.bottom > 84;
      const galleryPassed = gallery ? gallery.getBoundingClientRect().bottom < window.innerHeight * 0.92 : true;
      const userStartedScroll = window.scrollY > 96;
      const canShowSticky = window.innerWidth <= 749 ? userStartedScroll && !purchaseVisible : userStartedScroll && galleryPassed && !purchaseVisible;
      this.stickyBar.classList.toggle('is-visible', canShowSticky);
    };

    updateSticky();
    window.addEventListener('scroll', updateSticky, { passive: true });
    window.addEventListener('resize', updateSticky);

    this.stickyBar.addEventListener('submit', async (event) => {
      event.preventDefault();
      const formData = new FormData(this.stickyBar);
      const variant = this.getCurrentVariant();
      if (!variant?.available) return;
      await addItemsAndMaybeCheckout({
        items: [{ id: Number(formData.get('id')), quantity: 1 }],
        analyticsItem: buildEcommerceItem({
          variantId: variant?.id,
          productId: this.productId,
          title: this.productTitle,
          brand: this.productVendor,
          variantTitle: variant?.name,
          price: variant?.price,
          quantity: 1
        })
      });
    });

  }

  trackViewItem() {
    const variant = this.getCurrentVariant();
    if (!variant) return;
    pushDataLayer('view_item', {
      ecommerce: {
        currency: window.EaseHoundTheme?.currency || 'USD',
        value: Number(variant.price || 0) / 100,
        items: [
          buildEcommerceItem({
            variantId: variant.id,
            productId: this.productId,
            title: this.productTitle,
            brand: this.productVendor,
            variantTitle: variant.name,
            price: variant.price,
            quantity: 1
          })
        ]
      }
    });
  }
}

class ProductRecommendations extends HTMLElement {
  connectedCallback() {
    this.loadRecommendations();
  }

  async loadRecommendations() {
    const url = this.dataset.url;
    if (!url) return;
    const response = await fetch(url);
    const text = await response.text();
    const html = new DOMParser().parseFromString(text, 'text/html');
    const recommendations = html.querySelector('product-recommendations');
    if (recommendations?.innerHTML.trim().length) {
      this.innerHTML = recommendations.innerHTML;
    } else {
      this.remove();
    }
  }
}

class SearchSurface {
  constructor(root) {
    this.root = root;
    this.input = root.querySelector('[data-predictive-search-input]');
    this.results = root.querySelector('[data-predictive-search-results]');
    this.toggleButtons = document.querySelectorAll('[data-search-toggle]');
    this.boundClose = (event) => this.onOutsideClick(event);
  }

  init() {
    this.toggleButtons.forEach((button) => button.addEventListener('click', () => this.toggle()));
    this.input?.addEventListener('input', () => this.onInput());
  }

  toggle(forceOpen) {
    const shouldOpen = typeof forceOpen === 'boolean' ? forceOpen : this.root.hasAttribute('hidden');
    this.root.hidden = !shouldOpen;
    if (shouldOpen) {
      this.input?.focus();
      document.addEventListener('click', this.boundClose);
    } else {
      document.removeEventListener('click', this.boundClose);
    }
  }

  onOutsideClick(event) {
    if (!this.root.contains(event.target) && !event.target.closest('[data-search-toggle]')) {
      this.toggle(false);
    }
  }

  async onInput() {
    const query = this.input?.value?.trim();
    if (!query || !this.results) {
      if (this.results) this.results.innerHTML = '';
      return;
    }

    const url = `${window.EaseHoundTheme?.routes?.predictiveSearch || '/search/suggest'}?q=${encodeURIComponent(query)}&resources[type]=product,article,page,collection&resources[limit]=6&section_id=predictive-search&resources[options][unavailable_products]=hide`;

    try {
      const response = await fetch(url, { headers: { Accept: 'application/json' } });
      const data = await response.json();
      const groups = data.resources?.results || {};
      const items = ['products', 'collections', 'articles', 'pages']
        .flatMap((key) => (groups[key] || []).map((item) => ({ ...item, resourceType: key })))
        .slice(0, 6);

      if (!items.length) {
        this.results.innerHTML = '<div class="site-search__result"><strong>No quick matches yet</strong><span class="section-copy">Press search to explore the full results page.</span></div>';
        return;
      }

      this.results.innerHTML = items
        .map((item) => `
          <a class="site-search__result" href="${item.url}" data-search-result-link>
            <strong>${item.title}</strong>
            <span class="section-copy">${item.resourceType.replace(/s$/, '')}</span>
          </a>
        `)
        .join('');

      pushDataLayer('search', {
        search_term: query
      });
    } catch (error) {
      this.results.innerHTML = '';
    }
  }
}

const initScrollDepthTracking = () => {
  const milestones = [25, 50, 75, 100];
  const sent = new Set();

  const onScroll = () => {
    const scrollTop = window.scrollY;
    const maxScroll = document.documentElement.scrollHeight - window.innerHeight;
    if (maxScroll <= 0) return;
    const depth = Math.round((scrollTop / maxScroll) * 100);

    milestones.forEach((milestone) => {
      if (depth >= milestone && !sent.has(milestone)) {
        sent.add(milestone);
        pushDataLayer('scroll_depth', {
          scroll_depth: milestone,
          page_type: window.EaseHoundTheme?.pageType
        });
      }
    });
  };

  window.addEventListener('scroll', onScroll, { passive: true });
};

const trackVisibleProductLists = () => {
  const cards = [...document.querySelectorAll('[data-product-card]')];
  if (!cards.length) return;

  pushDataLayer('view_item_list', {
    ecommerce: {
      currency: window.EaseHoundTheme?.currency || 'USD',
      items: cards.map((card, index) => ({
        item_id: card.dataset.productId,
        item_name: card.dataset.productTitle,
        item_brand: card.dataset.productVendor,
        price: Number(card.dataset.productPrice || 0) / 100,
        index: index + 1,
        item_list_name: document.querySelector('h1')?.textContent.trim() || 'EaseHound products'
      }))
    }
  });
};

document.addEventListener('DOMContentLoaded', async () => {
  initDeliveryWindows();
  document.querySelectorAll('[data-mobile-nav-toggle]').forEach((button) => {
    button.addEventListener('click', () => {
      const nav = document.querySelector('[data-site-nav]');
      nav?.classList.toggle('is-open');
    });
  });

  document.querySelectorAll('[data-home-anchor]').forEach((link) => {
    link.addEventListener('click', () => {
      document.querySelector('[data-site-nav]')?.classList.remove('is-open');
    });
  });

  const searchSurfaceRoot = document.querySelector('[data-search-surface]');
  if (searchSurfaceRoot) {
    new SearchSurface(searchSurfaceRoot).init();
  }

  document.querySelectorAll('[data-product-form]').forEach((form) => new ProductFormHandler(form));

  document.querySelectorAll('[data-quick-add]').forEach((form) => {
    form.addEventListener('submit', async (event) => {
      event.preventDefault();
      const formData = new FormData(form);
      const quantity = Number(formData.get('quantity')) || 1;
      await addItemsAndMaybeCheckout({
        items: [
          {
            id: Number(formData.get('id')),
            quantity
          }
        ],
        analyticsItem: buildEcommerceItem({
          variantId: form.dataset.variantId,
          productId: form.dataset.productId,
          title: form.dataset.productTitle,
          brand: form.dataset.productVendor,
          price: form.dataset.productPrice,
          quantity
        })
      });
      if (form.matches('[data-cart-upsell-form]')) {
        pushDataLayer('cart_upsell_interaction', {
          item_id: form.dataset.variantId,
          item_name: form.dataset.productTitle,
          action: 'add'
        });
      }
    });
  });

  document.querySelectorAll('[data-static-bundle-add]').forEach((button) => {
    button.addEventListener('click', async () => {
      const bundleTitle = button.dataset.bundleTitle || 'EaseHound setup';
      const bundleProperties = { 'Recommended setup': bundleTitle };
      const items = String(button.dataset.bundleVariantIds || '')
        .split(',')
        .map((id) => Number(id.trim()))
        .filter(Boolean)
        .map((id) => ({ id, quantity: 1, properties: bundleProperties }));

      if (!items.length) return;

      const originalLabel = button.textContent;
      button.disabled = true;
      button.textContent = 'Adding setup...';

      try {
        const bundleVariantIds = items.map((item) => item.id);
        if (await cartAlreadyHasVariants(bundleVariantIds)) {
          await openCartWithNotice('This bundle is already in your cart. Adjust quantities or remove items before checkout.');
          return;
        }

        await addItemsAndMaybeCheckout({ items });
        pushDataLayer('add_bundle_to_cart', {
          bundle_name: bundleTitle,
          bundle_size: items.length
        });
      } finally {
        button.disabled = false;
        button.textContent = originalLabel;
      }
    });
  });

  document.querySelectorAll('[data-static-bundle-card]').forEach((card) => {
    card.addEventListener('click', (event) => {
      if (event.target.closest('a')) return;
      const bundleStack = card.closest('.bundle-offers__stack');
      bundleStack?.querySelectorAll('[data-static-bundle-card]').forEach((deal) => {
        deal.classList.toggle('is-selected', deal === card);
      });
    });
  });

  document.querySelectorAll('[data-begin-checkout]').forEach((button) => {
    button.addEventListener('click', async () => {
      const response = await fetch(`${window.Shopify.routes.root}cart.js`);
      const cart = await response.json();
      pushDataLayer('begin_checkout', {
        ecommerce: {
          currency: window.EaseHoundTheme?.currency || 'USD',
          value: Number(cart.total_price || 0) / 100,
          items: cart.items.map((item) =>
            buildEcommerceItem({
              variantId: item.variant_id,
              productId: item.product_id,
              title: item.product_title,
              brand: item.vendor,
              variantTitle: item.variant_title,
              price: item.price,
              quantity: item.quantity
            })
          )
        }
      });
    });
  });

  const cartDrawer = document.querySelector('cart-drawer');
  if (cartDrawer) {
    cartDrawer.close();
    await cartDrawer.render();
  }

  document.addEventListener('click', (event) => {
    const productLink = event.target.closest('.product-card a[href*="/products/"]');
    if (productLink) {
      pushDataLayer('select_item', {
        link_url: productLink.href
      });
    }

    const navLink = event.target.closest('.site-nav a');
    if (navLink) {
      pushDataLayer('navigation_click', {
        navigation_label: navLink.textContent.trim()
      });
    }

    const recommendationLink = event.target.closest('product-recommendations a[href*="/products/"]');
    if (recommendationLink) {
      pushDataLayer('recommendation_click', {
        link_url: recommendationLink.href
      });
    }

    const comparisonLink = event.target.closest('.comparison-actions a, .collection-comparison a, a[href="#collection-comparison"]');
    if (comparisonLink) {
      pushDataLayer('comparison_interaction', {
        link_url: comparisonLink.href,
        comparison_label: comparisonLink.textContent.trim()
      });
    }

    const reviewControl = event.target.closest('.jdgm-widget a, .jdgm-widget button, [data-judgeme-product-reviews] a, [data-judgeme-product-reviews] button');
    if (reviewControl) {
      pushDataLayer('review_interaction', {
        action: reviewControl.textContent.trim().slice(0, 80) || 'review control'
      });
    }

    const sizeGuide = event.target.closest('[data-size-guide] summary');
    if (sizeGuide) {
      pushDataLayer('size_guide_interaction', {
        item_name: document.querySelector('[data-product-section]')?.dataset.productTitle || '',
        action: sizeGuide.parentElement.open ? 'close' : 'open'
      });
    }

    const searchResultLink = event.target.closest('[data-search-result-link]');
    if (searchResultLink) {
      pushDataLayer('search_result_click', {
        link_url: searchResultLink.href
      });
    }
  });

  initScrollDepthTracking();
  trackVisibleProductLists();
});

if (!customElements.get('cart-drawer')) {
  customElements.define('cart-drawer', CartDrawer);
}

if (!customElements.get('product-recommendations')) {
  customElements.define('product-recommendations', ProductRecommendations);
}
